import { useCallback, useRef, useState, useSyncExternalStore } from "react";
import type { Task, PlanEvent } from "../../../shared/types";
import { createTask, createPlanEvent } from "../../../shared/types";
import {
  importTasks,
  getSnapshot as getTasksSnapshot,
  subscribe as subscribeTasks,
} from "../../tasks/store/task-store";
import {
  importEvents,
  getSnapshot as getEventsSnapshot,
  subscribe as subscribeEvents,
} from "../../events/store/event-store";

const DEMO_TAG = "__tutorial_demo__";

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function buildDemoTasks(): Task[] {
  const today = todayStr();
  return [
    { ...createTask("Faire les courses", "personal", today), tags: [DEMO_TAG] },
    {
      ...createTask("Préparer la présentation", "professional", today),
      tags: [DEMO_TAG],
    },
    {
      ...createTask("Méditer 10 minutes", "spiritual", today),
      tags: [DEMO_TAG],
    },
  ];
}

function buildDemoEvents(): PlanEvent[] {
  const today = todayStr();
  return [
    {
      ...createPlanEvent("Réunion d'équipe", "professional", today, "10:00", "11:00"),
      tags: [DEMO_TAG],
      description: "Point hebdomadaire avec l'équipe",
    },
    {
      ...createPlanEvent("Sport en salle", "personal", today, "18:00", "19:30"),
      tags: [DEMO_TAG],
    },
  ];
}

function isDemoItem(item: { tags: readonly string[] }): boolean {
  return item.tags.includes(DEMO_TAG);
}

export function useTutorialDemo() {
  const injectedRef = useRef({ tasks: false, events: false });
  const [startTime] = useState(() => Date.now());

  const tasks = useSyncExternalStore(subscribeTasks, getTasksSnapshot);
  const events = useSyncExternalStore(subscribeEvents, getEventsSnapshot);

  const injectTasks = useCallback(() => {
    if (injectedRef.current.tasks) return;
    injectedRef.current.tasks = true;
    // startTime is set at hook init via useState
    const current = getTasksSnapshot();
    importTasks([...buildDemoTasks(), ...current]);
  }, []);

  const injectEvents = useCallback(() => {
    if (injectedRef.current.events) return;
    injectedRef.current.events = true;
    const current = getEventsSnapshot();
    importEvents([...buildDemoEvents(), ...current]);
  }, []);

  const cleanup = useCallback(() => {
    if (injectedRef.current.tasks) {
      importTasks(getTasksSnapshot().filter((t) => !isDemoItem(t)));
      injectedRef.current.tasks = false;
    }
    if (injectedRef.current.events) {
      importEvents(getEventsSnapshot().filter((e) => !isDemoItem(e)));
      injectedRef.current.events = false;
    }
  }, []);

  const hasDemoTaskStatusChanged = tasks
    .filter(isDemoItem)
    .some((t) => t.status !== "todo");

  const hasNewTaskBeenCreated = tasks.some(
    (t) => !isDemoItem(t) && t.createdAt > startTime,
  );

  const hasNewEventBeenCreated = events.some(
    (t) => !isDemoItem(t) && t.createdAt > startTime,
  );

  return {
    injectTasks,
    injectEvents,
    cleanup,
    hasDemoTaskStatusChanged,
    hasNewTaskBeenCreated,
    hasNewEventBeenCreated,
  };
}
